<template>
  <v-dialog v-model="shown" persistent width="400px">
    <v-card>
      <v-card-text v-if="fatalError">
        <v-alert error icon="warning" value="true">{{ fatalError }}</v-alert>
      </v-card-text>
      <template v-else>
        <v-card-title class="headline">
          <span v-if="recording">{{ $t('player.title') }}</span>
          <span v-else>
            <span v-if="!privateKey">{{ $t('player.titlePrivateKeySettings') }}</span>
            <span v-if="privateKey && !decoding"> {{ $t('player.titlePrivateKeyPass') }}</span>
          </span>
        </v-card-title>

        <v-card-text v-if="recording && shown" class="text-xs-center">
          <audio
            ref="player"
            style="width: 100%"
            @timeupdate="timeUpdated"
            @canplay="canPlay = true"
            @pause="paused=true"
            @play="paused=false"
          >
            <source :src="recording" :type="audioType" @error="playError">
          </audio>

          <loadable :loading="!canPlay">
            <v-layout row wrap align-center>
              <v-flex xs3>
                <v-btn icon class="ma-0" @click="play" v-show="paused"><v-icon>play_arrow</v-icon></v-btn>
                <v-btn icon class="ma-0" @click="pause" v-show="!paused"><v-icon>pause</v-icon></v-btn>
                <v-btn icon class="ma-0" @click="stop"><v-icon>stop</v-icon></v-btn>
              </v-flex>
              <v-flex xs4 style="margin-left: auto" class="pr-3">
                <v-slider prepend-icon="volume_up" hide-details class="py-0" :min="0" :max="100" v-model="volume"></v-slider>
              </v-flex>
            </v-layout>

            <v-slider hide-details class="px-3 py-2" :value="currentTime" @input="changeTime" :min="0" :max="100"></v-slider>
          </loadable>
        </v-card-text>

        <v-card-text v-if="!recording && record && record.encrypted">
          <loadable :loading="decoding" :description="$t('player.decryptingInProgress')">
            <template v-if="!privateKey">
              <div @drop="drop">
                <p>{{ $t('player.privateKeyPrompt') }}</p>

                <v-text-field
                  v-model="privateKeyForm"
                  multi-line
                  :label="$t('player.privateKey')"
                  :error-messages="privateKeyErrors"
                  hide-details
                />

                <input type="file" ref="file" @change="fileChanged" class="my-2" />

                <v-switch
                  v-model="saveKey"
                  :label="$t('player.saveLocally')"
                />
                <div class="text-xs-center">
                  <v-btn @click="applyKey" flat>{{ $t('player.continue') }}</v-btn>
                </div>
              </div>
            </template>
            <template v-else>
              <p>{{ $t('player.passPrompt') }}</p>

              <v-text-field
                v-model="privateKeyPass"
                type="password"
                :label="$t('player.passphrase')"
                hide-details
                :error-messages="privateKeyPassErrors"
              />

              <v-switch
                v-model="saveKeyPass"
                :label="$t('player.saveTemporary')"
              />

              <div class="text-xs-center">
                <v-btn class="red--text darken-1" flat="flat" @click.native="clearKey">{{ $t('player.forgetKey') }}</v-btn>
                <v-btn @click="applyPass" flat><v-icon left>lock_open</v-icon> {{ $t('player.decrypt') }}</v-btn>
              </div>
            </template>
          </loadable>
        </v-card-text>
      </template>

      <v-card-actions>
        <v-btn v-show="recording && record && record.encrypted" class="red--text darken-1" flat="flat" @click.native="clearKey">{{ $t('player.forgetKey') }}</v-btn>
        <v-spacer></v-spacer>
        <v-btn v-show="recording && !fatalError" class="green--text darken-1" flat="flat" @click.native="download">{{ $t('player.download') }}</v-btn>
        <v-btn class="darken-1" flat="flat" @click.native="shown = false">{{ $t('close') }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import Record from '@/models/Record'
import Local from '@/store/local'

import Decoder, { InvalidKeyException, InvalidPassphraseException, DecodingFailedException, FileNotFoundException } from '@/utils/Decoder'
import Downloader from '@/utils/Downloader'

const codecTypeExt = {
  PCM: 'wav',
  FLAC: 'flac',
  MP3: 'mp3',
  G711: 'wav'
}

export default {
  props: {
    // Database object containing all informations about recording that is supposed to be played
    record: Object
  },
  data () {
    return {
      // Indicates if this dialog is shown
      shown: false,

      // Current time in recording player
      currentTime: 0,
      // Indicates time, which was naturally updated when playing (not user seeked)
      updating: null,
      // Indicates if player can play current recording
      canPlay: false,
      // Indicates if recording is paused
      paused: true,
      // Recording playback volume (0-100)
      volume: 100,

      // URL to recording decrypted contents
      recording: null,
      // Blob with recording decrypted contents
      recordingBlob: null,

      // Contents of private key form input
      privateKeyForm: null,
      // Saved private key contents
      privateKey: null,
      // Saved private key passphrase
      privateKeyPass: null,

      // Used to indicate if user wants to save private key
      saveKey: true,
      // Used to indicate if user wants to save passphrase
      saveKeyPass: false,

      // List of issues with private key
      privateKeyErrors: [],
      // List of issues with passphrase
      privateKeyPassErrors: [],

      // Indicates if we're currently decoding recording
      decoding: false,

      // Indicates if this dialog was opened to only download recording, not play it, in witch case it will close upon decoding file
      downloadOnly: false,

      // Cointains error message, if there's fatal unresolvable error when decoding/downloading recording
      fatalError: null
    }
  },
  watch: {
    /**
     * Changes when user tries to play new recording
     */
    record () {
      // Only do something when we actually have recording to play
      if (this.record) {
        // When the recording is encrypted, we need key and passphrase before doing anything
        if (this.record.encrypted) {
          // Try to load stored key and passphrase
          let stored = Local.get('private')

          // If there're stored key/passphrase, try to decrypt
          if (stored && stored.key) {
            // This is used to warn user about having saved key/passhrase
            this.$store.player.keyStored = true
            if (stored.pass) {
              this.$store.player.passStored = true
            }

            // Load private key and passphrase from store
            this.privateKey = this.privateKeyForm = stored.key
            this.privateKeyPass = stored.pass

            // Try to decrypt recording
            this.decode()
          } else {
            // Make sure form input is empty
            this.privateKeyForm = null
          }
        } else {
          // Unencrypted recording only needs to be downloaded and converted
          this.decode()
        }
      } else {
        // Clear data, when no record is to be played
        this.privateKey = null
        this.privateKeyPass = null
        this.recording = null
      }
    },

    /**
     * Changes when URL to decoded recording changes
     */
    recording () {
      if (this.$refs.player) {
        this.$refs.player.load()
      }
    },

    /**
     * Changes when user changes volume
     */
    volume () {
      if (this.$refs.player) {
        this.$refs.player.volume = this.volume / 100
      }
    },

    /**
     * Changes when visibility of this dialog changes
     */
    shown () {
      if (!this.shown) {
        this.$emit('closed')
      } else {
        this.$emit('opened')
      }
    }
  },
  computed: {
    /**
     * Computes correct mime type for recording.
     */
    audioType () {
      if (!this.record) return 'audio/x-wav'

      switch (this.record.codecType) {
        case 'MP3':
          return 'audio/mpeg'
        case 'FLAC':
          return 'audio/flac'
        case 'PCM':
        case 'G711':
        default:
          return 'audio/x-wav'
      }
    }
  },
  methods: {
    /**
     * Starts recording playback
     */
    play () {
      this.$refs.player.play()
    },

    /**
     * Pauses recording playback.
     */
    pause () {
      this.$refs.player.pause()
    },

    /**
     * Pauses recording playback and seeks to start.
     */
    stop () {
      this.$refs.player.pause()
      this.$refs.player.currentTime = 0
    },

    /**
     * Tries to download current recording. Called when users click on download button.
     */
    download () {
      // Make copy of db record, because we will be modifying it
      let item = { ...this.record }

      // Change extension to decrypted value
      item.fileName = Record.getBasename(item) + '.' + (codecTypeExt[item.codecType] || 'wav')
      // Save blob data, downloader will know what to do
      item.blob = this.recordingBlob

      // This creates archive with recording metadata and forces user to download it
      Downloader
        .downloadRecordings([item], Record.getBasename(item) + '.zip')
        .catch(error => {
          console.error('[Player]', 'Downloading failed')
          console.error(error)

          this.fatalError = this.$t('player.loadingFailed')
        })

      this.hide()
    },

    /**
     * Shows player dialog.
     * @param {boolean} downloadOnly if the dialog should only download recording, not play it
     */
    show (downloadOnly) {
      this.paused = true
      this.volume = 100
      this.currentTime = 0
      this.canPlay = false
      this.downloadOnly = downloadOnly || false
      this.shown = true
      this.fatalError = null
    },

    /**
     * Hides this dialog
     */
    hide () {
      this.shown = false
      this.privateKey = null
      this.privateKeyPass = null
      this.recording = null
      this.fatalError = null
    },

    /**
     * Seeks in recording.
     * @param {number} value
     */
    changeTime (value) {
      if (value !== Math.floor(this.updating) && this.$refs.player.duration) {
        this.$refs.player.currentTime = (value / 100) * this.$refs.player.duration
      }
    },

    /**
     * Called when time moves in player.
     */
    timeUpdated () {
      if (this.$refs.player) {
        this.currentTime = this.updating = (this.$refs.player.currentTime / this.$refs.player.duration) * 100
      }
    },

    /**
     * Called when user saves private key.
     */
    applyKey () {
      this.privateKey = this.privateKeyForm

      // Save key if prompted
      if (this.saveKey) {
        this.$store.player.keyStored = true

        // Save key to local storage
        Local.set('private', {
          key: this.privateKey,
          pass: null,
          username: this.$store.auth.username
        })
      }
    },

    applyPass () {
      // Save passphrase if prompted
      if (this.saveKeyPass) {
        this.$store.player.passStored = true

        let saved = Local.get('private')
        saved.pass = this.privateKeyPass
        Local.set('private', saved)
      }

      // Last user step, now try to decode the recording
      this.decode()
    },

    /**
     * Called when recording is encrypted and all required fields are entered.
     */
    decode () {
      this.decoding = true

      Decoder
        .decode(this.record, this.privateKey, this.privateKeyPass, this.downloadOnly ? 'download' : 'play')
        .then(result => {
          // Save for downloading purposes
          this.recordingBlob = result

          if (this.downloadOnly) {
            this.download()
          } else {
            // This is used in audio tag
            this.recording = window.URL.createObjectURL(this.recordingBlob)
          }
        })
        .catch(error => {
          console.log('[Player]', 'Error caught when decoding recording:', error)

          this.decoding = false

          // Key is invalid, prompt user to enter valid key
          if (error instanceof InvalidKeyException) {
            this.privateKeyErrors = [ this.$t('player.wrongKey') ]
            this.privateKey = null

            return
          }

          // Passphrase is invalid, prompt user to enter correct passphrase
          if (error instanceof InvalidPassphraseException) {
            this.privateKeyPassErrors = [ this.$t('player.keyRequiresPass') ]
            this.privateKeyPass = null

            return
          }

          // Failed to load recording data
          if (error instanceof FileNotFoundException) {
            this.fatalError = this.$t('player.loadingFailed')

            return
          }

          // Unknown error occured
          if (error instanceof DecodingFailedException) {
            alert(this.$t('player.playError', [ this.record.codecType || 'WAV' ]))

            return
          }
        })
    },

    /**
     * Clears all user data.
     */
    clearKey () {
      this.privateKey = null
      this.privateKeyForm = null
      this.privateKeyPass = null
      this.saveKey = false
      this.recording = null

      this.$store.player.keyStored = false
      this.$store.player.passStored = false

      Local.set('private', {})
    },

    /**
     * Handles files dropping on this dialog. Used for entering private keys.
     * @param {any} ev
     */
    drop (ev) {
      let data = ev.dataTransfer
      if (data.items) {
        for (var i = 0; i < data.items.length; i++) {
          if (data.items[i].kind === 'file') {
            this.readFile(data.items[i].getAsFile())
            ev.preventDefault()
            break
          }
        }
      } else {
        this.readFile(data.files[0])
        ev.preventDefault()
      }
    },

    /**
     * Handles file input. Used for entering private keys.
     */
    fileChanged () {
      if (this.$refs.file.files && this.$refs.file.files.length === 1) {
        this.readFile(this.$refs.file.files[0])
      }
    },

    /**
     * Reads contents of specified file and saves it as private key.
     * @param {any} file
     */
    readFile (file) {
      let reader = new FileReader()
      reader.onload = () => {
        this.privateKeyForm = reader.result
      }
      reader.readAsText(file)
    },

    /**
     * Error occured when trying to play file.
     * @param {any} e
     */
    playError (e) {
      alert(this.$t('player.playError', [this.record.codecType || 'WAV']))
      this.hide()
    }
  }
}
</script>

<style>
.input-group--slider.input-group--prepend-icon .slider {
  margin-left: 16px;
}
</style>



// WEBPACK FOOTER //
// Player.vue?1d2693e4